import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { isMaintenance, onMaintenanceChange, signalMaintenance } from '../maintenance';
import VendoraLogo from './VendoraLogo';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const CHECK_EVERY_MS = 10000;

// While an update runs, an app that was already open shows THE maintenance page — the same file
// nginx serves to anyone opening the site during a deploy (frontend_v2/public/maintenance.html),
// so there is one page, not two look-alikes. It is shown full-screen in a frame; the service
// worker keeps a copy, so it shows even if the server is fully down for a moment.
// Meanwhile the app checks the server every 10 seconds and reloads as soon as it answers normally
// again — the reload also picks up the new version. (The page's own Refresh button and
// countdown reload this whole window too.)
function UpdatingScreen() {
    useEffect(() => {
        let stopped = false;
        const check = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/health`, { cache: 'no-store' });
                const type = res.headers.get('content-type') || '';
                if (res.ok && type.includes('application/json')) {
                    if (!stopped) window.location.reload();
                }
            } catch { /* still updating */ }
        };
        const timer = setInterval(check, CHECK_EVERY_MS);
        return () => { stopped = true; clearInterval(timer); };
    }, []);

    return (
        <Box component="iframe" src="/maintenance.html" title="Vendora is being updated"
            sx={{ position: 'fixed', inset: 0, width: '100%', height: '100%', border: 0, zIndex: 2000,
                  bgcolor: '#0b2b6b' }} />
    );
}

// Safety net: any crash shows a friendly screen instead of a blank white page. A failure to
// load part of the app usually means a new version is being put in place — treat that as an
// update rather than an error.
class CrashGuard extends React.Component {
    constructor(props) {
        super(props);
        this.state = { crashed: false };
    }

    static getDerivedStateFromError() {
        return { crashed: true };
    }

    componentDidCatch(error) {
        const msg = String(error?.name || '') + ' ' + String(error?.message || '');
        if (/ChunkLoadError|Loading chunk|Loading CSS chunk|Unexpected token '?<'?/i.test(msg)) {
            signalMaintenance();
        }
        console.error('App crashed:', error);
    }

    render() {
        if (!this.state.crashed) return this.props.children;
        if (isMaintenance()) return <UpdatingScreen />;
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
                       justifyContent: 'center', gap: 2, px: 3, textAlign: 'center' }}>
                <VendoraLogo size={56} />
                <Typography variant="h6" fontWeight={800}>Something went wrong</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                    Please reload the page. If it keeps happening, tap Help and tell us.
                </Typography>
                <Button variant="contained" onClick={() => window.location.reload()}
                    sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44, px: 3 }}>
                    Reload
                </Button>
            </Box>
        );
    }
}

export default function MaintenanceGate({ children }) {
    const [updating, setUpdating] = useState(isMaintenance());
    useEffect(() => onMaintenanceChange(setUpdating), []);
    if (updating) return <UpdatingScreen />;
    return <CrashGuard>{children}</CrashGuard>;
}
